import { useQuery } from '@tanstack/react-query';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { useSelectedTarget, useSelectedCompany } from '../store/projectStore';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import { getAuthHeaders } from '../utils/authHeaders';

export type ApplicationCredentialOption = {
  id: string;
  username: string;
  role: string;
};

type UseApplicationCredentialOptionsParams = {
  skip?: boolean;
};

export function useApplicationCredentialOptions({
  skip = false,
}: UseApplicationCredentialOptionsParams = {}) {
  const selectedTarget = useSelectedTarget();
  const selectedCompany = useSelectedCompany();

  return useQuery({
    queryKey: ['application-credentials', selectedTarget?.id],
    queryFn: async (): Promise<ApplicationCredentialOption[]> => {
      if (!selectedTarget?.id || !selectedCompany?.tenantId) {
        return [];
      }

      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.getCredentials({
        params: { id: selectedTarget.id },
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
        },
        extraHeaders: {
          ...authHeaders,
        },
      });

      if (response.status !== 200) {
        throw new Error('Failed to fetch credentials');
      }

      const credentials = response.body.credentials;
      if (!Array.isArray(credentials)) {
        return [];
      }

      return credentials.map((credential) => ({
        id: credential.id,
        username: credential.username,
        role: credential.role,
      }));
    },
    enabled: !skip && !!selectedTarget?.id && !!selectedCompany?.tenantId,
    refetchOnWindowFocus: false,
  });
}
